// Page for map
import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import { Info } from '@mui/icons-material';

import '../style/map.scss';
import {
  clearMarkers,
  getLngLatMidpoints,
  addMainRouteWayPoints,
  addColorCodedMainRouteWaypoints,
} from '../utilities/mapHelperFunctions/mapHelperFunctions';
import {
  addSIGMETareas,
  removeSIGMETareas,
  addTurbulenceRouteWaypoints,
  addEnrouteAirports,
  addAltAirports,
  addAreas,
} from '../utilities/mapHelperFunctions';
import { DISABLE_SWIPE } from '../utilities/enums';
import { getTKOFSettings } from '../utilities/perfCalcHelperFunctions';
import {
  TurbulenceIndicator,
  SlideOutMenuNew,
  InfoModal,
  AirportMapModal,
  SigmetModal,
} from '../components';

// access token should be in a .env file
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

// global array of markers that are cleared and re-populated on filter change for map
let mapMarkers = [];
// Track layers and sources for efficient cleanup
let mapLayers = new Set();
let mapSources = new Set();

const filters = [{ title: 'RESET' }, { title: 'ALTN' }, { title: 'ENROUTE' }];

export const Map = (props) => {
  const [filter, setFilter] = useState(filters[0].title);
  const [areaCategories, setAreaCategories] = useState([]);
  const [visibleAreas, setVisibleAreas] = useState([]);
  const [sigmetModalData, setSigmetModalData] = useState();
  const [airportModalData, setAirportModalData] = useState();
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [showSigmet, setShowSigmet] = useState(true);
  const [tkofSettings, setTkofSettings] = useState(null);

  const [openMenu, setOpenMenu] = useState(null);
  const handleMenuToggle = (menuType) => {
    setOpenMenu((current) => (current === menuType ? null : menuType));
  };

  const handleChecked = (checkbox, checked) => {
    setAreaCategories((prevState) =>
      prevState.map((area) =>
        area.label === checkbox.label ? { ...area, checked: checked } : area
      )
    );
  };

  // Used for handling data fetches for different states
  useEffect(() => {
    // Get take off settings
    if (props.data?.sACReg) {
      getTKOFSettings(props.data.sACReg, setTkofSettings, props.parseString);
    }
  }, [props.data]);

  useEffect(() => {
    // Reduces areas to a list containing their unique categories, formatted to be used for checkboxes in slide out menu
    if (props.areaMessages?.length) {
      const areaCategories = props.areaMessages.reduce((acc, currArea) => {
        if (acc.find((area) => area.label === currArea.sCat?.[0])) {
          return acc;
        } else {
          const checkbox = {
            label: currArea.sCat?.[0],
            checked: currArea.bDefaultVisible?.[0] === 'true' ? true : false,
            handleChecked,
          };
          return [...acc, checkbox];
        }
      }, []);

      // Add static SIGMET category only if normal mode
      if (!props.isTrainingMode) {
        const staticCategory = {
          label: 'SIGMET',
          checked: true,
          handleChecked,
        };
        setAreaCategories([staticCategory, ...areaCategories]);
      } else {
        setAreaCategories(areaCategories);
      }
    }
  }, [props.areaMessages, props.isTrainingMode]);

  useEffect(() => {
    if (props.areaMessages?.length && areaCategories.length) {
      // Handle static area categories
      const showSigmet = areaCategories.some(
        (area) => area.checked && area.label === 'SIGMET'
      );
      setShowSigmet(showSigmet);

      // Handle dynamic area categories
      const checkedDynamicCategories = areaCategories.filter(
        (area) => area.checked
      );
      const filteredAreas = props.areaMessages.filter((area) =>
        checkedDynamicCategories.some((cat) => cat.label === area.sCat?.[0])
      );

      setVisibleAreas(filteredAreas);
    }
  }, [props.areaMessages, areaCategories]);


  const handleOpenInfoModal = () => setIsInfoModalOpen(true);
  const handleCloseInfoModal = () => setIsInfoModalOpen(false);

  const handleOpenAirportModal = (
    airportName,
    airportIATA,
    airportMETAR,
    airportTAF,
    airportICAO,
    notamList
  ) => {
    setAirportModalData({
      airportName,
      airportIATA,
      airportMETAR,
      airportTAF,
      airportICAO,
      notamList,
    });
  };
  const handleCloseAirportModal = () => setAirportModalData(undefined);

  const handleOpenSigmetModal = (data) => setSigmetModalData(data);
  const handleCloseSigmetModal = () => setSigmetModalData(undefined);

  const clearMap = () => {
    // clears all elements from the map
    clearMarkers(mapMarkers);

    // Remove tracked layers
    mapLayers.forEach((layerId) => {
      if (map.current.getLayer(layerId)) {
        // Remove event listeners for interactive layers
        map.current.off('mouseenter', layerId);
        map.current.off('mouseleave', layerId);
        map.current.off('click', layerId);
        map.current.removeLayer(layerId);
      }
    });

    // Remove tracked sources
    mapSources.forEach((sourceId) => {
      if (map.current.getSource(sourceId)) {
        map.current.removeSource(sourceId);
      }
    });

    // Clear the tracking sets
    mapLayers.clear();
    mapSources.clear();
  };

  const mapContainer = useRef(null);
  const map = useRef(null);
  const midpoints = getLngLatMidpoints(props.data);

  // Sets default state of the map on and draws the main route on the map on first render
  useEffect(() => {
    if (map.current) return; // initialize map only once
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [midpoints.lng, midpoints.lat],
      zoom: 3,
    });
    map.current.on('load', () => { });
    map.current.on('zoom', function () {
      const hiddenWaypointTexts = document.querySelectorAll(
        '.route-waypoint-text'
      );
      if (map.current.getZoom() >= 4) {
        hiddenWaypointTexts.forEach((hiddenText) => {
          hiddenText.classList.add('show');
        });
      } else {
        hiddenWaypointTexts.forEach((hiddenText) => {
          hiddenText.classList.remove('show');
        });
      }
    });
  }, [midpoints.lat, midpoints.lng]);

  // Logic for different filters are run when flight changes or the filter changes
  // Clears map and redraws based on filter
  // Use requestAnimationFrame for better performance than setTimeout
  useEffect(() => {
    if (!navigator.onLine || !props.data || !map.current) return;

    let cancelled = false;

    const renderMap = () => {
      if (cancelled || !map.current) return;
      // Ensure the map style is loaded before modifying sources/layers
      if (!map.current.isStyleLoaded()) {
        // If style not loaded yet, wait for it
        map.current.once('idle', renderMap);
        return;
      }

      clearMap();

      // Skip turbulence and sigmet data in training mode.
      // SIGMETs are not cleared with the rest of the map, so a redraw does not blink them.
      const skipTurbulenceAndSigmet = props.isTrainingMode;
      if (showSigmet && !skipTurbulenceAndSigmet) {
        addSIGMETareas(map, handleOpenSigmetModal);
      } else {
        removeSIGMETareas(map);
      }

      if (filter === 'RESET') {
        addMainRouteWayPoints(
          map,
          props.data,
          mapMarkers,
          handleOpenAirportModal,
          mapLayers,
          mapSources
        );
        if (!skipTurbulenceAndSigmet) {
          addTurbulenceRouteWaypoints(
            map,
            props.data,
            mapMarkers,
            handleOpenAirportModal,
            mapLayers,
            mapSources
          );
        }
        addAreas(map, visibleAreas, handleOpenSigmetModal);
      } else if (filter === 'ALTN') {
        addColorCodedMainRouteWaypoints(
          map,
          props.data,
          mapMarkers,
          handleOpenAirportModal,
          mapLayers,
          mapSources
        );
        addAltAirports(
          map,
          props.data,
          mapMarkers,
          props.reqFuel,
          handleOpenAirportModal,
          mapLayers,
          mapSources
        );
        if (!skipTurbulenceAndSigmet) {
          addTurbulenceRouteWaypoints(
            map,
            props.data,
            mapMarkers,
            handleOpenAirportModal,
            mapLayers,
            mapSources
          );
        }
        addAreas(map, visibleAreas, handleOpenSigmetModal);
      } else if (filter === 'ENROUTE') {
        addColorCodedMainRouteWaypoints(
          map,
          props.data,
          mapMarkers,
          handleOpenAirportModal,
          mapLayers,
          mapSources
        );
        addEnrouteAirports(
          map,
          props.data,
          mapMarkers,
          handleOpenAirportModal,
          mapLayers,
          mapSources
        );
        if (!skipTurbulenceAndSigmet) {
          addTurbulenceRouteWaypoints(
            map,
            props.data,
            mapMarkers,
            handleOpenAirportModal,
            mapLayers,
            mapSources
          );
        }
        addAreas(map, visibleAreas, handleOpenSigmetModal);
      }
    };

    // Use requestIdleCallback for non-urgent updates, with setTimeout fallback.
    // Cancel the previous callback so a burst of state updates redraws the map once.
    let idleId = 0;
    if ('requestIdleCallback' in window) {
      idleId = requestIdleCallback(renderMap, { timeout: 2000 });
    } else {
      idleId = requestAnimationFrame(renderMap);
    }

    return () => {
      cancelled = true;
      if ('cancelIdleCallback' in window) cancelIdleCallback(idleId);
      else cancelAnimationFrame(idleId);
      if (map.current) map.current.off('idle', renderMap);
    };
  }, [filter, visibleAreas, props.data, props.isTrainingMode, showSigmet]);

  return (
    <div className={DISABLE_SWIPE}>
      <InfoModal
        isOpen={isInfoModalOpen}
        handleClose={handleCloseInfoModal}
        category={filter}
      />
      <AirportMapModal
        isOpen={!!airportModalData}
        handleClose={handleCloseAirportModal}
        data={airportModalData}
        adList={
          tkofSettings?.ADList && tkofSettings?.ADList?.length > 0
            ? tkofSettings?.ADList[0]?.string
            : []
        } // Used for RWY info
      />
      <SigmetModal
        isOpen={!!sigmetModalData}
        handleClose={handleCloseSigmetModal}
        data={sigmetModalData}
      />

      {!props.isCabinCrew && (
        <SlideOutMenuNew
          side='right'
          title='filter'
          backgroundColor='#003D50'
          height='20vh'
          maxHeight='7rem'
          topPosition='10%'
          menuItems={filters}
          navigate={(index) => setFilter(filters[index].title)}
          checkboxes={areaCategories}
          openMenu={openMenu}
          onMenuToggle={handleMenuToggle}
          setOpenMenu={setOpenMenu}
        />
      )}
      <div>
        {!props.isTrainingMode && (
          <TurbulenceIndicator
            data={props.data}
            isCabinCrew={props.isCabinCrew}
          />
        )}
        <div ref={mapContainer} className='map-container' />

        <div
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            zIndex: 10,
            color: 'black',
            cursor: 'pointer',
          }}
          onClick={handleOpenInfoModal}
        >
          <Info fontSize='large' />
        </div>
      </div>
    </div>
  );
};
